import type { ReactNode } from "react";

import { FeatureGrid, NoteBand, Page } from "../../shared/ui/Page";
import {
  CONTACT_SECTIONS,
  OPEN_SOURCE_NOTICE_SECTIONS,
  PRIVACY_POLICY_SECTIONS,
  SAFETY_SECTIONS,
  TERMS_SECTIONS
} from "./legalSupportContent";
import { PRICING_FAQS, PRICING_PLANS } from "./pricingContent";
import { WHITEPAPER_SECTIONS } from "./privacySecurityContent";

interface AudienceCard {
  readonly title: string;
  readonly body: string;
}

const audienceCards: readonly AudienceCard[] = [
  {
    title: "Clubs & teams",
    body: "Coordinate match day from the sideline when there's no signal, then sort the season during the week. The roster stays with the club, not a social network."
  },
  {
    title: "Schools & parents",
    body: "Run the class group, the excursion, the fundraiser, without building a searchable record that outlives the school year."
  },
  {
    title: "Workplaces & teams",
    body: "Shift swaps, floor coordination, the all-hands offsite. Day-to-day logistics that do not need to live on a vendor's servers to work."
  },
  {
    title: "Communities & organisers",
    body: "Gather a crowd, plan with your people, act together, and leave no convenient archive for anyone else to comb through later."
  },
  {
    title: "Events & crews",
    body: "Wristband the build crew into a Space, coordinate across a loud field, wind it down when the gear is packed."
  },
  {
    title: "Friends & households",
    body: "The trip, the share house, the band. A small group that wants to talk without feeding another feed."
  }
];

export function HomePage() {
  return (
    <main className="apple-site">
      <HeroSection
        eyebrow="Private coordination for real-world groups"
        title={<>Some things should <br />stay in the room.</>}
        intro="Booply is how the groups that actually meet - clubs, teams, classes, communities - talk and get organised. Together, it connects you directly over Bluetooth. Apart, an encrypted relay carries the rest. Nothing is kept longer than it needs to be."
        primaryCta={{ href: "/download", label: "Get Booply" }}
        secondaryCta={{ href: "/how-it-works", label: "See how it works" }}
      >
        <ProximityGraphic />
      </HeroSection>

      <FeatureRow
        eyebrow="Nearby"
        eyebrowTone="near"
        title="When you're together, the room is the network."
        body="On the bus, in the hall, out on the field - Booply links your group phone to phone over Bluetooth. No signal. No servers. No one in the middle. Just the people who are actually there."
        visual={<NearbyNetworkGraphic />}
        mist
      />

      <FeatureRow
        eyebrow="Relay"
        eyebrowTone="relay"
        title="Apart, and still just between you."
        body="Walk out of range and Booply hands off to an encrypted relay. It passes your messages along and can read none of them. A bridge, not a destination."
        visual={<RelayBridgeGraphic />}
        flip
      />

      <CenteredSection
        eyebrow="Built to forget"
        title="Coordination, not a permanent record."
        intro="Booply is for sorting things out, not storing your life. Messages do their job and clear out on a short schedule. Your group's history was never meant to be someone's product."
        mist
      />

      <FeatureRow
        eyebrow="Spaces & Rooms"
        title="Your group. You decide who's in it."
        body="A Space is your group, and admins approve who joins. Rooms keep the different things you're juggling apart - the committee, the carpool, the gear list - so one group can run a dozen conversations without the chaos."
        visual={<SpacesRoomsGraphic />}
        cta={{ href: "/product", label: "More on Spaces" }}
      />

      <AudienceSection previewOnly />

      <CenteredSection
        eyebrow="Privacy"
        title="Booply doesn't want your data."
        intro="No ad model. No profile of you. No quiet harvesting of who you talk to. The group stays the group's business - by design, not by promise."
        cta={{ href: "/privacy-security", label: "How privacy works" }}
      />

      <DownloadCloser />
    </main>
  );
}

export function ProductPage() {
  return (
    <main className="apple-site">
      <HeroSection
        eyebrow="Spaces & Rooms"
        title={<>Structure without <br />the overhead.</>}
        intro="A Space holds your whole group. Rooms keep its moving parts apart. Admins decide who gets in. That's the entire system, and it's enough."
      />

      <FeatureRow
        eyebrow="Spaces"
        title="One group, one Space."
        body="The footy club. The Year 4 parents. The Tuesday night band. A Space is the whole of it, and joining isn't automatic - an admin approves every person who comes in. No stray numbers, no randoms, no one you didn't mean to add."
        visual={<ApprovalGraphic />}
        mist
      />

      <FeatureRow
        eyebrow="Rooms"
        title="A place for each thing."
        body="Inside a Space, Rooms split the conversations that shouldn't be tangled together. Match day in one. The committee in another. The gear list somewhere it can't bury anything important. People only see the Rooms that are theirs."
        visual={<RoomRowsGraphic />}
        flip
      />

      <section className="apple-section apple-section--mist">
        <div className="apple-wrap apple-journey-wrap">
          <p className="apple-eyebrow">Joining</p>
          <h2 className="apple-display apple-display--section">Invite by QR, code or link. Then approve.</h2>
          <p className="apple-lede">
            Sharing a Space should be easy. Joining one should still be intentional.
            Booply keeps those two ideas separate.
          </p>
          <div className="journey">
            <div className="stop"><span>Share</span><strong>QR, code or link</strong></div>
            <div className="arrowdot">→</div>
            <div className="stop"><span>Review</span><strong>A person asks in</strong></div>
            <div className="arrowdot">→</div>
            <div className="stop"><span>Approve</span><strong>The Space stays yours</strong></div>
          </div>
        </div>
      </section>

      <CenteredSection
        title="Set it up in a minute. Run it for years."
        cta={{ href: "/download", label: "Get Booply" }}
      />
    </main>
  );
}

export function PricingPage() {
  return (
    <main className="apple-site">
      <HeroSection
        eyebrow="Pricing"
        title={<>Free to start. <br />Paid when a group needs more room.</>}
        intro="Launch pricing is in USD until localisation. Plus gives one person more personal Spaces. Spaces gives organisers paid Space slots they assign to the groups they own."
      />

      <section className="apple-section apple-section--mist">
        <div className="apple-wrap">
          <div className="pricing-grid apple-pricing-grid">
            {PRICING_PLANS.map((plan, index) => (
              <article className={`apple-price-card${index === 2 ? " apple-price-card--featured" : ""}`} key={plan.name}>
                <span className="apple-card-chip">{plan.chip}</span>
                <h2>{plan.name}</h2>
                <strong>{plan.price}</strong>
                <ul>
                  {plan.homepageFeatures.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <a className="apple-pill apple-pill--dark" href="/download">
                  {plan.name === "Free" ? "Start free" : "Choose plan"}
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="apple-section">
        <div className="apple-wrap apple-copy-grid">
          <article>
            <p className="apple-eyebrow">Paid Space slots</p>
            <h2 className="apple-display apple-display--section">Upgrade the group, not the noise.</h2>
            <p className="apple-body">
              Spaces 1 gives you one paid slot. Spaces 3 gives you three. Assign
              each slot to one Space you own; paid Spaces do not count against
              your Free or Plus personal Space limit.
            </p>
          </article>
          <article>
            <p className="apple-eyebrow">Downgrades</p>
            <h2 className="apple-display apple-display--section">Cancelling doesn't break your group.</h2>
            <p className="apple-body">
              Paid features continue until the end of the billing period. If a
              paid Space later exceeds Free limits, it enters Free Limit Mode
              with a 14-day grace period.
            </p>
          </article>
        </div>
      </section>

      <section className="apple-section apple-section--mist">
        <div className="apple-wrap">
          <p className="apple-eyebrow">FAQ</p>
          <h2 className="apple-display apple-display--section">Small print, plain words.</h2>
          <div className="faq-grid apple-faq-grid">
            {PRICING_FAQS.map((item) => (
              <article className="faq-item" key={item.question}>
                <h2>{item.question}</h2>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export function DownloadPage() {
  return (
    <main className="apple-site">
      <HeroSection
        eyebrow="Get Booply"
        title={<>Get your group <br />on Booply.</>}
        intro="Free to start. Nothing to set up but the people you trust. Native iOS and Android store links will appear here when launch distribution is ready."
        primaryCta={{ href: "/pricing", label: "View pricing" }}
        secondaryCta={{ href: "/how-it-works", label: "See how it works" }}
      />
      <section className="apple-section apple-section--mist">
        <div className="apple-wrap app-store-row">
          <span className="store-badge"><span aria-hidden="true"></span><span><small>Download on the</small><b>App Store</b></span></span>
          <span className="store-badge"><span aria-hidden="true">▶</span><span><small>Get it on</small><b>Google Play</b></span></span>
        </div>
      </section>
    </main>
  );
}

export function HowItWorksPage() {
  return (
    <main className="apple-site">
      <HeroSection
        eyebrow="How it works"
        title={<>Close enough to talk. <br />Private enough to mean it.</>}
        intro="Booply has two ways to connect, and it picks for you. Together, over Bluetooth. Apart, over an encrypted relay. You never choose which - you just talk."
      />

      <FeatureRow
        eyebrow="1 · Nearby"
        eyebrowTone="near"
        title="No internet required."
        body="When your group is in range, your phones find each other over Bluetooth and talk directly. The basement hall with no signal, the campsite, the back of the bus - none of it matters. The people in the room are the network, and nothing leaves it."
        visual={<DirectLinkGraphic />}
        mist
      />

      <FeatureRow
        eyebrow="2 · Relay"
        eyebrowTone="relay"
        title="A bridge that can't read."
        body="Out of range, Booply routes through an encrypted relay. Messages are sealed on your device and only open again on your group's. The relay moves them along and sees nothing inside - it's plumbing, not a postbox someone reads."
        visual={<RelayBridgeGraphic />}
        flip
      />

      <section className="apple-section apple-section--mist">
        <div className="apple-wrap apple-journey-wrap">
          <p className="apple-eyebrow">3 · Short retention</p>
          <h2 className="apple-display apple-display--section">It clears out after itself.</h2>
          <p className="apple-lede">
            Messages stick around long enough to be useful, then go. There's no
            archive of your group quietly growing on a server somewhere, because
            there's no reason for one to exist.
          </p>
          <div className="journey">
            <div className="stop"><span>Together</span><strong>Phones connect directly</strong></div>
            <div className="arrowdot">→</div>
            <div className="stop"><span>Apart</span><strong>Encrypted relay carries it</strong></div>
            <div className="arrowdot">→</div>
            <div className="stop"><span>After</span><strong>Short retention clears it</strong></div>
          </div>
        </div>
      </section>

      <CenteredSection
        title="It just works the way a room does."
        intro="You talk to the people there, and when everyone leaves, the room is empty again."
        cta={{ href: "/download", label: "Get Booply" }}
      />
    </main>
  );
}

export function PrivacySecurityPage() {
  return (
    <main className="apple-site">
      <HeroSection
        eyebrow="Privacy"
        title={<>The group stays <br />the group's business.</>}
        intro="Most coordination apps make money knowing who you talk to and what about. Booply makes its money another way, so it never has to."
      />

      <section className="apple-section">
        <div className="apple-wrap">
          <ul className="promises">
            <li><span className="tick">✓</span><span><strong>Local-first.</strong> Your group lives on your devices, not on a server that owns the copy.</span></li>
            <li><span className="tick">✓</span><span><strong>Encrypted in transit.</strong> When the relay carries a message, it's sealed end to end.</span></li>
            <li><span className="tick">✓</span><span><strong>Bluetooth means offline.</strong> Together, nothing touches the internet at all.</span></li>
            <li><span className="tick">✓</span><span><strong>Short retention.</strong> Messages clear out on a schedule instead of piling up forever.</span></li>
            <li><span className="tick">✓</span><span><strong>Admin-approved membership.</strong> Nobody is in a Space unless a person let them in.</span></li>
            <li><span className="tick">✓</span><span><strong>No account to surrender.</strong> The less Booply knows about you, the less there is to lose.</span></li>
          </ul>

          <div className="never">
            <h2 className="apple-display apple-display--section">What Booply never does</h2>
            <ul>
              <li><span>✕</span> Sell ads against your conversations</li>
              <li><span>✕</span> Build a profile of who you know</li>
              <li><span>✕</span> Keep a permanent archive of your group</li>
              <li><span>✕</span> Read inside the relay</li>
              <li><span>✕</span> Hand your social graph to anyone</li>
              <li><span>✕</span> Make privacy a paid upgrade</li>
            </ul>
          </div>
        </div>
      </section>

      <CenteredSection
        title="Privacy you don't have to take on faith."
        intro="It's the architecture, not a clause buried in a policy."
        mist
        cta={{ href: "/whitepaper", label: "Read the overview" }}
      />
    </main>
  );
}

export function WhitepaperPage() {
  return (
    <Page
      eyebrow="Whitepaper"
      title="The practical shape of private coordination."
      intro="A plain-language overview of the product choices behind Booply: direct connection when groups are together, encrypted Relay when apart, short retention, paid Space slots and realistic security limits."
    >
      <section className="detail-list" aria-label="Booply whitepaper sections">
        {WHITEPAPER_SECTIONS.map((item, index) => (
          <article className="detail-row" key={item.title}>
            <span className="detail-index">{String(index + 1).padStart(2, "0")}</span>
            <div>
              <h2>{item.title}</h2>
              <p>{item.body}</p>
            </div>
          </article>
        ))}
      </section>
    </Page>
  );
}

export function SupportPage() {
  return (
    <main className="apple-site">
      <HeroSection
        eyebrow="For your group"
        title={<>For the groups <br />that meet in real life.</>}
        intro="Booply is built for clubs, teams, classes, organisers and small communities that need a private Space without turning every decision into a chaotic group chat."
        primaryCta={{ href: "/download", label: "Get Booply" }}
        secondaryCta={{ href: "/pricing", label: "View pricing" }}
      />
      <AudienceSection heading="Use it where coordination gets messy." />
      <CenteredSection
        title="Bring your group across."
        intro="Free to start. They'll be in within the minute."
        cta={{ href: "/download", label: "Get Booply" }}
      />
    </main>
  );
}

export function SafetyPage() {
  return (
    <Page
      eyebrow="Safety"
      title="Safety help for Booply Spaces."
      intro="Booply safety tools are intended to be practical, calm, and scoped to real group problems. Reported content is a deliberate exception to ordinary encrypted message handling."
    >
      <FeatureGrid items={SAFETY_SECTIONS} />
      <NoteBand title="Emergency help">
        <p>
          Booply is not an emergency service. If someone is in immediate danger,
          contact local emergency services.
        </p>
      </NoteBand>
    </Page>
  );
}

export function TermsPage() {
  return (
    <Page
      eyebrow="Legal"
      title="Terms draft"
      intro="These web Terms are a concise working draft based on the current product plan. They are not final lawyer-reviewed terms."
    >
      <FeatureGrid items={TERMS_SECTIONS} />
    </Page>
  );
}

export function PrivacyPage() {
  return (
    <Page
      eyebrow="Legal"
      title="Privacy policy draft"
      intro="This privacy policy is a concise working draft based on Booply’s current planning docs. It is not final lawyer-reviewed privacy language."
    >
      <FeatureGrid items={PRIVACY_POLICY_SECTIONS} />
    </Page>
  );
}

export function OpenSourceNoticesPage() {
  return (
    <Page
      eyebrow="Legal"
      title="Open source notices"
      intro="Booply is closed source, with transparent documentation and respectful attribution where required."
    >
      <FeatureGrid items={OPEN_SOURCE_NOTICE_SECTIONS} />
    </Page>
  );
}

export function ContactPage() {
  return (
    <Page
      eyebrow="Contact"
      title="Contact Booply."
      intro="Launch contact channels are reserved below. Live forms and response expectations will be confirmed before public release."
    >
      <FeatureGrid items={CONTACT_SECTIONS} />
    </Page>
  );
}

export function NotFoundPage() {
  return (
    <Page
      eyebrow="Not found"
      title="That Booply page is not here."
      intro="Use the navigation to return to the product site or admin shell."
    />
  );
}

interface HeroSectionProps {
  readonly eyebrow: string;
  readonly title: ReactNode;
  readonly intro: string;
  readonly children?: ReactNode;
  readonly primaryCta?: { readonly href: string; readonly label: string };
  readonly secondaryCta?: { readonly href: string; readonly label: string };
}

function HeroSection({ eyebrow, title, intro, children, primaryCta, secondaryCta }: HeroSectionProps) {
  return (
    <section className="apple-hero apple-wrap">
      <p className="apple-eyebrow">{eyebrow}</p>
      <h1 className="apple-display apple-display--hero">{title}</h1>
      <p className="apple-lede">{intro}</p>
      {(primaryCta || secondaryCta) && (
        <div className="apple-cta-row">
          {primaryCta && <a className="apple-pill apple-pill--grad" href={primaryCta.href}>{primaryCta.label}</a>}
          {secondaryCta && <a className="apple-text-link" href={secondaryCta.href}>{secondaryCta.label} <span>›</span></a>}
        </div>
      )}
      {children && <div className="apple-hero-art">{children}</div>}
    </section>
  );
}

interface FeatureRowProps {
  readonly eyebrow: string;
  readonly eyebrowTone?: "near" | "relay";
  readonly title: string;
  readonly body: string;
  readonly visual: ReactNode;
  readonly mist?: boolean;
  readonly flip?: boolean;
  readonly cta?: { readonly href: string; readonly label: string };
}

function FeatureRow({ eyebrow, eyebrowTone, title, body, visual, mist = false, flip = false, cta }: FeatureRowProps) {
  return (
    <section className={`apple-section${mist ? " apple-section--mist" : ""}`}>
      <div className={`apple-wrap apple-row${flip ? " apple-row--flip" : ""}`}>
        <div className="apple-copy">
          <p className={`apple-eyebrow${eyebrowTone ? ` apple-eyebrow--${eyebrowTone}` : ""}`}>{eyebrow}</p>
          <h2 className="apple-display apple-display--section">{title}</h2>
          <p className="apple-body">{body}</p>
          {cta && <a className="apple-text-link apple-row-link" href={cta.href}>{cta.label} <span>›</span></a>}
        </div>
        <div className="apple-art-panel">{visual}</div>
      </div>
    </section>
  );
}

interface CenteredSectionProps {
  readonly eyebrow?: string;
  readonly title: string;
  readonly intro?: string;
  readonly mist?: boolean;
  readonly cta?: { readonly href: string; readonly label: string };
}

function CenteredSection({ eyebrow, title, intro, mist = false, cta }: CenteredSectionProps) {
  return (
    <section className={`apple-section apple-centered${mist ? " apple-section--mist" : ""}`}>
      <div className="apple-wrap">
        {eyebrow && <p className="apple-eyebrow">{eyebrow}</p>}
        <h2 className="apple-display apple-display--section">{title}</h2>
        {intro && <p className="apple-lede">{intro}</p>}
        {cta && <div className="apple-cta-row"><a className="apple-pill apple-pill--grad" href={cta.href}>{cta.label}</a></div>}
      </div>
    </section>
  );
}

function AudienceSection({
  heading = "For the groups that meet in real life.",
  previewOnly = false
}: {
  readonly heading?: string;
  readonly previewOnly?: boolean;
}) {
  const cards = previewOnly ? audienceCards.slice(0, 4) : audienceCards;

  return (
    <section className="apple-section apple-section--mist">
      <div className="apple-wrap apple-centered">
        <p className="apple-eyebrow">For your group</p>
        <h2 className="apple-display apple-display--section">{heading}</h2>
        <div className="apple-card-grid">
          {cards.map((card) => (
            <article className="apple-card" key={card.title}>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
        {previewOnly && <a className="apple-text-link apple-row-link" href="/support">More groups <span>›</span></a>}
      </div>
    </section>
  );
}

function DownloadCloser() {
  return (
    <section className="apple-closer apple-section--mist">
      <div className="apple-wrap apple-centered">
        <h2 className="apple-display apple-display--section">Get your group on Booply.</h2>
        <p className="apple-lede">Free to start. Nothing to set up but the people you trust.</p>
        <div className="app-store-row">
          <span className="store-badge"><span aria-hidden="true"></span><span><small>Download on the</small><b>App Store</b></span></span>
          <span className="store-badge"><span aria-hidden="true">▶</span><span><small>Get it on</small><b>Google Play</b></span></span>
        </div>
      </div>
    </section>
  );
}

function ProximityGraphic() {
  return (
    <svg className="proximity-graphic" viewBox="0 0 760 300" role="img" aria-label="Two phones connecting directly when near, and through an encrypted relay when apart">
      <rect x="40" y="70" width="86" height="160" rx="18" fill="#0b0b0f" />
      <rect x="48" y="78" width="70" height="144" rx="12" fill="#11131a" />
      <circle cx="83" cy="150" r="20" fill="none" stroke="#2d9bff" strokeWidth="2.5" />
      <circle cx="83" cy="150" r="6" fill="#2d9bff" />
      <g fill="none" stroke="#2d9bff" strokeWidth="3" strokeLinecap="round">
        <path className="pulse" d="M150 150 q26 -34 52 0" />
        <path className="pulse d2" d="M168 150 q34 -46 68 0" />
        <path className="pulse d3" d="M186 150 q42 -58 84 0" />
      </g>
      <text x="83" y="258" textAnchor="middle" fill="#6e6e73" fontSize="13">you</text>
      <text x="222" y="258" textAnchor="middle" fill="#2d9bff" fontSize="13" fontWeight="600">nearby · bluetooth</text>
      <rect x="300" y="80" width="74" height="140" rx="16" fill="#0b0b0f" />
      <rect x="307" y="87" width="60" height="126" rx="10" fill="#11131a" />
      <circle cx="337" cy="150" r="14" fill="none" stroke="#2d9bff" strokeWidth="2.5" />
      <line x1="408" y1="150" x2="470" y2="150" stroke="#d2d2d7" strokeWidth="2" strokeDasharray="3 6" />
      <rect x="470" y="124" width="92" height="52" rx="14" fill="#fff" stroke="#7a5af8" strokeWidth="2" />
      <path d="M508 140v-4a8 8 0 0116 0v4" fill="none" stroke="#7a5af8" strokeWidth="2.4" />
      <rect x="502" y="140" width="28" height="22" rx="5" fill="#7a5af8" />
      <text x="516" y="196" textAnchor="middle" fill="#7a5af8" fontSize="12" fontWeight="600">encrypted relay</text>
      <line x1="562" y1="150" x2="624" y2="150" stroke="#d2d2d7" strokeWidth="2" strokeDasharray="3 6" />
      <rect x="634" y="80" width="74" height="140" rx="16" fill="#0b0b0f" />
      <rect x="641" y="87" width="60" height="126" rx="10" fill="#11131a" />
      <circle cx="671" cy="150" r="14" fill="none" stroke="#7a5af8" strokeWidth="2.5" />
      <text x="671" y="246" textAnchor="middle" fill="#6e6e73" fontSize="13">apart</text>
    </svg>
  );
}

function NearbyNetworkGraphic() {
  return (
    <svg viewBox="0 0 320 240" aria-label="Phones connected directly in a circle over Bluetooth">
      <g fill="none" stroke="#2d9bff" strokeWidth="2.5" strokeLinecap="round" opacity=".55">
        <line x1="160" y1="60" x2="80" y2="170" />
        <line x1="160" y1="60" x2="240" y2="170" />
        <line x1="80" y1="170" x2="240" y2="170" />
      </g>
      <circle className="pulse" cx="160" cy="60" r="6" fill="#2d9bff" />
      <circle className="pulse d2" cx="80" cy="170" r="6" fill="#2d9bff" />
      <circle className="pulse d3" cx="240" cy="170" r="6" fill="#2d9bff" />
      <g fill="#11131a" stroke="#2d9bff" strokeWidth="2">
        <rect x="144" y="34" width="32" height="52" rx="8" />
        <rect x="64" y="150" width="32" height="52" rx="8" />
        <rect x="224" y="150" width="32" height="52" rx="8" />
      </g>
    </svg>
  );
}

function RelayBridgeGraphic() {
  return (
    <svg viewBox="0 0 320 200" aria-label="Two distant phones connected through a locked relay">
      <rect x="30" y="64" width="40" height="72" rx="10" fill="#11131a" />
      <rect x="250" y="64" width="40" height="72" rx="10" fill="#11131a" />
      <line x1="78" y1="100" x2="130" y2="100" stroke="#d2d2d7" strokeWidth="2.5" strokeDasharray="2 7" />
      <line x1="190" y1="100" x2="242" y2="100" stroke="#d2d2d7" strokeWidth="2.5" strokeDasharray="2 7" />
      <rect x="130" y="74" width="60" height="52" rx="14" fill="#fff" stroke="#7a5af8" strokeWidth="2.5" />
      <path d="M152 92v-5a8 8 0 0116 0v5" fill="none" stroke="#7a5af8" strokeWidth="2.6" />
      <rect x="146" y="92" width="28" height="22" rx="5" fill="#7a5af8" />
    </svg>
  );
}

function SpacesRoomsGraphic() {
  return (
    <svg viewBox="0 0 300 240" aria-label="A Space containing several Rooms">
      <rect x="30" y="24" width="240" height="192" rx="20" fill="#fff" stroke="#d2d2d7" strokeWidth="2" />
      <text x="50" y="56" fill="#1d1d1f" fontSize="15" fontWeight="600">Northcote FC</text>
      <circle cx="246" cy="50" r="9" fill="none" stroke="#7a5af8" strokeWidth="2" />
      <path d="M242 50l3 3 5-6" fill="none" stroke="#7a5af8" strokeWidth="2" />
      <g fontSize="13" fill="#363639">
        <rect x="50" y="76" width="200" height="34" rx="10" fill="#f5f5f7" /><text x="64" y="98">#committee</text>
        <rect x="50" y="118" width="200" height="34" rx="10" fill="#f5f5f7" /><text x="64" y="140">#match-day</text>
        <rect x="50" y="160" width="200" height="34" rx="10" fill="#f5f5f7" /><text x="64" y="182">#gear-list</text>
      </g>
    </svg>
  );
}

function DirectLinkGraphic() {
  return (
    <svg viewBox="0 0 300 200" aria-label="Direct Bluetooth link, no cloud">
      <g fill="none" stroke="#2d9bff" strokeWidth="2.5" strokeLinecap="round">
        <path className="pulse" d="M120 100 q30 -36 60 0" />
        <path className="pulse d2" d="M120 100 q30 36 60 0" />
      </g>
      <rect x="74" y="74" width="34" height="54" rx="9" fill="#11131a" stroke="#2d9bff" strokeWidth="2" />
      <rect x="192" y="74" width="34" height="54" rx="9" fill="#11131a" stroke="#2d9bff" strokeWidth="2" />
    </svg>
  );
}

function ApprovalGraphic() {
  return (
    <svg viewBox="0 0 320 220" aria-label="A Space member approval card">
      <defs>
        <filter id="approval-soft-shadow" x="24" y="16" width="272" height="192" filterUnits="userSpaceOnUse">
          <feDropShadow dx="0" dy="12" stdDeviation="13" floodColor="#1d1d1f" floodOpacity="0.08" />
        </filter>
      </defs>
      <rect
        x="54"
        y="38"
        width="212"
        height="144"
        rx="24"
        fill="#ffffff"
        stroke="#d2d2d7"
        strokeWidth="1.4"
        filter="url(#approval-soft-shadow)"
      />
      <circle cx="84" cy="78" r="16" fill="#f5f5f7" stroke="#d2d2d7" strokeWidth="1" />
      <text x="84" y="83" textAnchor="middle" fontSize="13" fontWeight="600" fill="#6e6e73">A</text>
      <text x="110" y="74" fontSize="14.5" fontWeight="600" fill="#1d1d1f">Asha wants to join</text>
      <text x="110" y="95" fontSize="12.5" fill="#6e6e73">Year 4 - Room 12</text>

      <rect x="76" y="116" width="78" height="32" rx="16" fill="#1d1d1f" />
      <text x="115" y="137" textAnchor="middle" fontSize="12.5" fontWeight="500" fill="#ffffff">Approve</text>
      <rect x="164" y="116" width="78" height="32" rx="16" fill="#f5f5f7" />
      <text x="203" y="137" textAnchor="middle" fontSize="12.5" fontWeight="500" fill="#363639">Decline</text>
    </svg>
  );
}

function RoomRowsGraphic() {
  return (
    <svg viewBox="0 0 300 220" aria-label="A list of rooms inside a Space">
      <g fontSize="14" fill="#363639">
        <rect x="40" y="34" width="220" height="40" rx="12" fill="#f5f5f7" /><text x="60" y="59"># committee</text>
        <rect x="40" y="82" width="220" height="40" rx="12" fill="#eef2ff" /><text x="60" y="107" fill="#4c6fff"># match-day</text>
        <rect x="40" y="130" width="220" height="40" rx="12" fill="#f5f5f7" /><text x="60" y="155"># gear-list</text>
        <rect x="40" y="178" width="220" height="40" rx="12" fill="#f5f5f7" /><text x="60" y="203"># socials</text>
      </g>
    </svg>
  );
}
